<template>
  <div
    class="w-full flex flex-col h-[min(100dvh,1600px)] min-h-[820px] bg-gray-50 font-sans"
  >
    <!-- 常驻画布上方，可折叠为顶部细条 -->
    <header class="shrink-0 z-20 bg-white border-b border-slate-200 shadow-sm">
      <div
        v-if="panelCollapsed"
        class="flex flex-wrap items-center gap-2 px-6 py-2 min-h-[44px]"
      >
        <button
          type="button"
          class="panel-toggle-btn shrink-0"
          title="展开控制面板"
          @click="panelCollapsed = false"
        >
          展开面板
        </button>
        <button
          type="button"
          class="text-sm truncate flex-1 min-w-[120px] text-center rounded-md px-2 py-0.5 -mx-1 border-0 bg-transparent transition-colors"
          :class="
            copyableEffectCode
              ? 'text-slate-600 cursor-pointer hover:bg-slate-100 hover:text-slate-900'
              : 'text-slate-600 cursor-default'
          "
          :title="
            copyableEffectCode
              ? `点击复制 code：${copyableEffectCode}`
              : collapsedSummary
          "
          :disabled="!copyableEffectCode"
          @click="copyCollapsedEffectCode"
        >
          <span v-if="copyCodeFeedback" class="font-medium text-emerald-600">{{
            copyCodeFeedback
          }}</span>
          <span v-else>{{ collapsedSummary }}</span>
        </button>
        <div
          v-if="sourceMode === 'effects'"
          class="flex items-center gap-1 shrink-0"
        >
          <button
            type="button"
            class="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
            :disabled="filteredEffects.length < 2"
            title="上一条（当前筛选列表内循环；Q 或 Alt+←）"
            @click="goPrevEffect"
          >
            上一个
          </button>
          <span class="text-[11px] text-slate-500 tabular-nums px-0.5">
            {{ effectPositionLabel }}
          </span>
          <button
            type="button"
            class="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
            :disabled="filteredEffects.length < 2"
            title="下一条（当前筛选列表内循环；E 或 Alt+→）"
            @click="goNextEffect"
          >
            下一个
          </button>
        </div>
        <div
          v-else-if="sourceMode === 'jsonList'"
          class="flex items-center gap-1 shrink-0"
        >
          <button
            type="button"
            class="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
            :disabled="jsonListWorkflows.length < 2"
            title="上一项（列表内循环；Q 或 Alt+←）"
            @click="goPrevJsonListItem"
          >
            上一个
          </button>
          <span class="text-[11px] text-slate-500 tabular-nums px-0.5">
            {{ jsonListPositionLabel }}
          </span>
          <button
            type="button"
            class="px-2.5 py-1 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none"
            :disabled="jsonListWorkflows.length < 2"
            title="下一项（列表内循环；E 或 Alt+→）"
            @click="goNextJsonListItem"
          >
            下一个
          </button>
        </div>
        <button
          type="button"
          class="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600"
          @click="resetLayout"
        >
          重新排版
        </button>
      </div>

      <div v-else class="px-6 py-4 space-y-3">
        <div class="flex flex-wrap justify-between items-center gap-4">
          <div class="flex flex-wrap items-center gap-3 min-w-0">
            <button
              type="button"
              class="panel-toggle-btn shrink-0"
              title="折叠控制区，画布占满下方"
              @click="panelCollapsed = true"
            >
              折叠到顶部
            </button>
            <h1
              class="text-xl font-bold text-gray-800 flex items-center gap-2 min-w-0"
            >
              <svg
                class="w-6 h-6 text-blue-500 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                ></path>
              </svg>
              工作流节点编排
            </h1>
          </div>
          <div class="flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              class="px-5 py-2 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600 transition-colors shadow-sm"
              @click="resetLayout"
            >
              重新整理排版
            </button>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <div
            class="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-0.5 text-xs font-medium shadow-sm"
            role="tablist"
          >
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                sourceMode === 'effects'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="setSourceMode('effects')"
            >
              效果列表
            </button>
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                sourceMode === 'json'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="setSourceMode('json')"
            >
              JSON 粘贴
            </button>
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                sourceMode === 'jsonList'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="setSourceMode('jsonList')"
            >
              JSON 列表
            </button>
          </div>

          <div
            v-if="sourceMode === 'effects'"
            class="inline-flex rounded-lg border border-slate-300 bg-slate-100 p-0.5 text-xs font-medium shadow-sm"
            role="group"
            aria-label="按数据来源筛选"
          >
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                mediaFilter === 'all'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="mediaFilter = 'all'"
            >
              全部
            </button>
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                mediaFilter === 'video'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="mediaFilter = 'video'"
            >
              Video
            </button>
            <button
              type="button"
              class="rounded-md px-3 py-1.5 transition-colors"
              :class="
                mediaFilter === 'image'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="mediaFilter = 'image'"
            >
              Image
            </button>
          </div>
        </div>

        <div
          v-if="sourceMode === 'effects'"
          class="flex flex-wrap items-center gap-2"
        >
          <input
            v-model.trim="searchKeyword"
            type="text"
            placeholder="搜索 code / title（命中后自动选第一条）"
            class="w-64 h-9 px-3 text-sm border border-slate-300 rounded-md bg-slate-50 text-slate-800 shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white"
          />
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none h-9"
              :disabled="filteredEffects.length < 2"
              title="上一条（Q 或 Alt+←）"
              @click="goPrevEffect"
            >
              上一个
            </button>
            <button
              type="button"
              class="px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none h-9"
              :disabled="filteredEffects.length < 2"
              title="下一条（E 或 Alt+→）"
              @click="goNextEffect"
            >
              下一个
            </button>
            <span class="text-[11px] text-slate-500 tabular-nums px-1">
              {{ effectPositionLabel }}
            </span>
          </div>
          <select
            v-model="selectedEffectKey"
            class="h-9 min-w-[280px] max-w-[50vw] flex-1 px-3 text-sm border border-slate-300 rounded-md bg-slate-50 text-slate-800 shadow-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white"
            @change="handleEffectChange"
          >
            <option disabled value="">请选择一个 workflow</option>
            <option
              v-for="item in filteredEffects"
              :key="getEffectUniqueKey(item)"
              :value="getEffectUniqueKey(item)"
            >
              {{ item.title || item.code }} ({{ item.effect_type || "-" }} /
              {{ item.code }})
            </option>
          </select>
          <span class="text-xs text-gray-500"
            >共 {{ effectsList.length }} 条 · 当前筛选
            {{ filteredEffects.length }} 条</span
          >
        </div>

        <div v-else-if="sourceMode === 'json'" class="space-y-2">
          <textarea
            v-model="jsonInputText"
            rows="14"
            spellcheck="false"
            placeholder="粘贴 workflow JSON（需含 nodes；可与效果列表结构一致）"
            class="w-full min-h-[200px] px-3 py-2 text-xs font-mono border border-slate-300 rounded-md bg-slate-50 text-slate-800 shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white resize-y"
          />
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition-colors shadow-sm"
              @click="applyJsonToCanvas"
            >
              解析并渲染到画布
            </button>
            <span class="text-xs text-gray-500"
              >解析失败时会在画布区域显示错误信息</span
            >
          </div>
        </div>

        <div v-else class="space-y-2">
          <textarea
            v-model="jsonListInputText"
            rows="14"
            spellcheck="false"
            placeholder='粘贴 JSON 数组，如 [{ "nodes": {...}, "output": {...} }, ...]；每个元素为一条含 nodes 的 workflow 对象'
            class="w-full min-h-[200px] px-3 py-2 text-xs font-mono border border-slate-300 rounded-md bg-slate-50 text-slate-800 shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white resize-y"
          />
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              class="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition-colors shadow-sm"
              @click="applyJsonListToCanvas"
            >
              解析列表并渲染第一项
            </button>
            <div class="flex items-center gap-1">
              <button
                type="button"
                class="px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none h-9"
                :disabled="jsonListWorkflows.length < 2"
                title="上一项（Q 或 Alt+←）"
                @click="goPrevJsonListItem"
              >
                上一个
              </button>
              <button
                type="button"
                class="px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none h-9"
                :disabled="jsonListWorkflows.length < 2"
                title="下一项（E 或 Alt+→）"
                @click="goNextJsonListItem"
              >
                下一个
              </button>
              <span class="text-[11px] text-slate-500 tabular-nums px-1">
                {{ jsonListPositionLabel }}
              </span>
            </div>
            <span class="text-xs text-gray-500"
              >某项无效会提示是第几条；Q / E 或 Alt+← / Alt+→ 切换</span
            >
          </div>
        </div>

        <p v-if="effectsLoading" class="text-xs text-gray-500">
          正在加载效果数据...
        </p>
        <p v-else-if="effectsError" class="text-xs text-red-500">
          {{ effectsError }}
        </p>
        <p v-else-if="activeEffectMeta" class="text-xs text-gray-500">
          当前：{{ activeEffectMeta.title || "-" }} /
          {{ activeEffectMeta.effect_type || "-" }} /
          {{ activeEffectMeta.code || "-" }}
          <span
            v-if="sourceMode === 'effects' && selectedEffect?._mediaSource"
            class="ml-1 text-slate-400"
          >
            · 来源 {{ selectedEffect._mediaSource }}
          </span>
          <span
            v-if="sourceMode === 'jsonList' && jsonListWorkflows.length"
            class="ml-1 text-slate-400"
          >
            · 列表 {{ jsonListPositionLabel }}
          </span>
        </p>
      </div>
    </header>

    <div class="flex-1 min-h-0 w-full relative">
      <div
        v-if="parseError"
        class="absolute inset-0 flex items-center justify-center p-6 text-red-600 text-sm"
      >
        {{ parseError }}
      </div>
      <VueFlow
        v-else
        class="h-full w-full"
        v-model:nodes="nodes"
        v-model:edges="edges"
        :default-viewport="{ zoom: 0.8 }"
        :min-zoom="0.1"
        :max-zoom="4"
        fit-view-on-init
      >
        <template #node-userInput="flowProps">
          <div
            class="w-72 bg-amber-50 border-2 border-amber-300 rounded-lg shadow-md transition-all"
          >
            <div
              class="bg-amber-100/50 px-4 py-3 border-b border-amber-200 rounded-t-lg font-bold text-amber-800 flex items-center gap-2"
            >
              <span class="text-lg">🧑‍💻</span> 全局输入 (User Input)
            </div>
            <div class="p-4 space-y-3">
              <div
                v-for="outputKey in flowProps.data.outputs"
                :key="outputKey"
                class="relative p-2 bg-white border border-amber-100 rounded text-sm font-mono text-amber-700 shadow-sm flex items-center justify-between"
              >
                <span>{{ outputKey }}</span>
                <Handle
                  type="source"
                  :id="getSourceHandleId(outputKey)"
                  position="right"
                  class="w-3.5 h-3.5 bg-amber-500 border-2 border-white -right-2"
                />
              </div>
            </div>
          </div>
        </template>

        <template #node-finalOutput="flowProps">
          <div
            class="w-72 bg-emerald-50 border-2 border-emerald-300 rounded-lg shadow-md transition-all"
          >
            <div
              class="bg-emerald-100/50 px-4 py-3 border-b border-emerald-200 rounded-t-lg font-bold text-emerald-800 flex items-center gap-2"
            >
              <span class="text-lg">🎉</span> 最终输出 (Final Output)
            </div>
            <div class="p-4 space-y-3">
              <div
                v-for="(val, key) in flowProps.data.outputs"
                :key="key"
                class="relative p-2 bg-white border border-emerald-100 rounded text-sm font-mono text-emerald-700 shadow-sm flex items-center justify-between"
              >
                <Handle
                  type="target"
                  :id="getTargetHandleId(key)"
                  position="left"
                  class="w-3.5 h-3.5 bg-emerald-500 border-2 border-white -left-2"
                />
                <span>{{ key }}</span>
              </div>
            </div>
          </div>
        </template>

        <template #node-custom="flowProps">
          <div
            class="w-[380px] bg-white border border-gray-200 rounded-lg shadow-lg transition-all hover:shadow-xl relative"
          >
            <Handle
              type="target"
              :id="GENERIC_TARGET_HANDLE_ID"
              position="left"
              class="w-4 h-4 bg-gray-300 border-2 border-white left-[-8px] top-[26px] z-10"
            />

            <Handle
              v-for="(outKey, oidx) in flowProps.data.outputKeys"
              :key="String(outKey)"
              type="source"
              :id="getSourceHandleId(outKey)"
              position="right"
              class="w-4 h-4 bg-blue-500 border-2 border-white right-[-8px] z-10"
              :style="{ top: `${26 + oidx * 28}px` }"
            />

            <div
              class="bg-gray-50 px-4 py-3 border-b border-gray-200 rounded-t-lg flex justify-between items-center"
            >
              <div
                class="font-bold text-gray-800 truncate"
                :title="flowProps.data.title"
              >
                {{ flowProps.data.title }}
              </div>
              <div
                class="flex flex-col gap-1 text-[11px] font-mono font-medium"
              >
                <div
                  class="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-center"
                >
                  {{ flowProps.data.app }}
                </div>
                <div
                  class="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-center"
                >
                  {{ flowProps.data.app_name }}
                </div>
              </div>
            </div>

            <div
              v-if="getVisibleAppKeyEntries(flowProps.data.app_key_data).length"
              class="px-3 py-2 border-b border-dashed border-slate-200 bg-pink-100 max-h-52 overflow-y-auto"
            >
              <div
                class="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2"
              >
                model_config
              </div>
              <div class="space-y-2">
                <div
                  v-for="[ak, entry] in getVisibleAppKeyEntries(
                    flowProps.data.app_key_data
                  )"
                  :key="ak"
                  class="rounded-md border border-slate-200 bg-pink-100 px-2.5 py-2 text-[11px] leading-snug"
                >
                  <div
                    class="font-mono font-semibold text-slate-800 mb-1.5 break-all"
                  >
                    {{ ak }}
                  </div>
                  <div class="space-y-1 font-mono text-[10px] text-slate-600">
                    <div class="flex flex-wrap gap-x-1 gap-y-0.5">
                      <span class="text-slate-400 shrink-0">type:</span>
                      <span
                        class="text-indigo-800 font-medium break-all"
                        :class="{
                          'text-slate-300 font-normal': !isAppKeyTypeValid(
                            entry?.type
                          ),
                        }"
                      >
                        {{
                          isAppKeyTypeValid(entry?.type)
                            ? String(entry.type)
                            : "—"
                        }}
                      </span>
                    </div>
                    <div class="flex flex-wrap gap-x-1 gap-y-0.5 items-start">
                      <span class="text-slate-400 shrink-0">options:</span>
                      <span
                        class="break-all text-slate-700 min-w-0"
                        :class="{
                          'text-slate-300': !isAppKeyOptionsValid(
                            entry?.options
                          ),
                        }"
                      >
                        {{
                          isAppKeyOptionsValid(entry?.options)
                            ? formatAppKeyOptions(entry.options)
                            : "—"
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="p-4 space-y-4">
              <div
                v-for="(val, key) in flowProps.data.inputs"
                :key="key"
                class="relative border-l-2 border-blue-100 pl-3 group"
              >
                <Handle
                  type="target"
                  :id="getTargetHandleId(key)"
                  position="left"
                  class="w-3.5 h-3.5 bg-green-500 border-2 border-white -left-[9px] top-3 opacity-80 group-hover:opacity-100 transition-opacity"
                />

                <div
                  class="font-medium text-gray-700 mb-1.5 flex items-center gap-2 text-sm"
                >
                  <span
                    class="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs font-mono"
                    >{{ key }}</span
                  >
                  <span
                    class="text-[10px] text-gray-400 border border-gray-200 px-1 rounded"
                    >{{ getTypeLabel(val) }}</span
                  >
                </div>

                <div v-if="Array.isArray(val)" class="space-y-1.5">
                  <div
                    v-for="(item, idx) in val"
                    :key="idx"
                    class="text-gray-600 bg-gray-50 p-2 rounded text-xs break-all border border-gray-100"
                  >
                    <VariableText :text="item" />
                  </div>
                </div>

                <div
                  v-else-if="
                    typeof val === 'boolean' || typeof val === 'number'
                  "
                  class="text-gray-800 font-mono text-xs font-semibold"
                >
                  {{ val }}
                </div>

                <div
                  v-else
                  class="text-xs bg-gray-50 p-2 rounded border border-gray-100"
                >
                  <div
                    :class="{
                      'line-clamp-3': !isExpanded(`${flowProps.id}-${key}`),
                    }"
                  >
                    <VariableText :text="val" />
                  </div>
                  <button
                    v-if="String(val).length > 80"
                    type="button"
                    class="text-blue-500 hover:text-blue-700 mt-1.5 text-[11px] font-medium flex items-center transition-colors"
                    @click="toggleExpand(`${flowProps.id}-${key}`)"
                  >
                    {{
                      isExpanded(`${flowProps.id}-${key}`)
                        ? "收起内容 ▲"
                        : "展开全部 ▼"
                    }}
                  </button>
                </div>
              </div>
            </div>
            <div
              v-if="flowProps.data.bgm_url"
              class="px-3 py-2 border-b border-dashed border-slate-200 bg-pink-100 max-h-52 overflow-y-auto"
            >
              <div
                class="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2"
              >
                bgm_url
              </div>
              <div class="space-y-2">
                <div
                  class="rounded-md border text-stone-950 border-stone-200 bg-pink-100 px-2.5 py-2 text-[11px] leading-snug"
                >
                  {{ flowProps.data.bgm_url }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <Background pattern-color="#94a3b8" :gap="24" :size="1.5" />
      </VueFlow>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  reactive,
  defineComponent,
  h,
  nextTick,
  computed,
  watch,
} from "vue";
import { VueFlow, useVueFlow, Handle } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";
import dagre from "dagre";

const nodes = ref([]);
const edges = ref([]);
const parseError = ref("");
const GENERIC_TARGET_HANDLE_ID = "in:generic";
const { fitView, updateNodeInternals } = useVueFlow();
const expandedState = reactive(new Set());
const sourceMode = ref("effects"); // 'effects' | 'json' | 'jsonList'
const jsonInputText = ref("");
/** JSON 列表模式：粘贴的数组原文 */
const jsonListInputText = ref("");
/** 解析通过后的 workflow 对象列表（与 parseWorkflowConfig 结果同形） */
const jsonListWorkflows = ref([]);
const jsonListIndex = ref(0);
const panelCollapsed = ref(false);
/** 全部 | 仅 video index | 仅 image index */
const mediaFilter = ref("all"); // 'all' | 'video' | 'image'
const searchKeyword = ref("");
const selectedEffectKey = ref("");
const effectsList = ref([]);
const effectsLoading = ref(false);
const effectsError = ref("");
const activeEffectMeta = ref(null);
const r2CachedFetch = useR2CachedFetch();

const getEffectType = (item) => String(item?.effect_type || "unknown");

const getEffectUniqueKey = (item) =>
  `${getEffectType(item)}::${String(item?.code || "")}`;

const filteredEffects = computed(() => {
  let list = effectsList.value;
  const mf = mediaFilter.value;
  if (mf !== "all") {
    list = list.filter((item) => item?._mediaSource === mf);
  }
  const keyword = searchKeyword.value.trim().toLowerCase();
  if (!keyword) return list;
  return list.filter((item) => {
    const code = String(item?.code || "").toLowerCase();
    const title = String(item?.title || "").toLowerCase();
    return code.includes(keyword) || title.includes(keyword);
  });
});

const selectedIndexInFiltered = computed(() => {
  const list = filteredEffects.value;
  const idx = list.findIndex(
    (item) => getEffectUniqueKey(item) === selectedEffectKey.value
  );
  return idx;
});

const effectPositionLabel = computed(() => {
  const n = filteredEffects.value.length;
  if (n === 0) return "0/0";
  const i = selectedIndexInFiltered.value;
  if (i < 0) return `?/${n}`;
  return `${i + 1}/${n}`;
});

const jsonListPositionLabel = computed(() => {
  const n = jsonListWorkflows.value.length;
  if (n === 0) return "0/0";
  const i = jsonListIndex.value;
  const safe = Math.min(Math.max(0, i), n - 1);
  return `${safe + 1}/${n}`;
});

const collapsedSummary = computed(() => {
  if (sourceMode.value === "json") return "JSON 粘贴 · 点击展开编辑";
  if (sourceMode.value === "jsonList") {
    const n = jsonListWorkflows.value.length;
    if (!n) return "JSON 列表 · 点击展开解析";
    const meta = activeEffectMeta.value;
    const t = meta?.title || meta?.code || "workflow";
    return `${t} · ${jsonListPositionLabel.value}`;
  }
  if (effectsLoading.value) return "正在加载效果…";
  const meta = activeEffectMeta.value;
  if (meta) {
    const t = meta.title || meta.code || "-";
    const c = meta.code || "-";
    return `${t} · ${c} · ${effectPositionLabel.value}`;
  }
  return "工作流节点编排";
});

/** 折叠条摘要可点击复制时使用的 code（仅效果列表模式；JSON 粘贴不复制） */
const copyableEffectCode = computed(() => {
  if (sourceMode.value !== "effects") return "";
  const meta = activeEffectMeta.value;
  if (!meta) return "";
  const c = meta.code;
  if (c === undefined || c === null) return "";
  const s = String(c).trim();
  return s;
});

const copyCodeFeedback = ref("");

async function copyCollapsedEffectCode() {
  const code = copyableEffectCode.value;
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
    copyCodeFeedback.value = "已复制 code";
    setTimeout(() => {
      copyCodeFeedback.value = "";
    }, 1400);
  } catch (err) {
    console.error("复制 code 失败:", err);
    copyCodeFeedback.value = "复制失败";
    setTimeout(() => {
      copyCodeFeedback.value = "";
    }, 1400);
  }
}

function goPrevEffect() {
  if (sourceMode.value !== "effects") return;
  const list = filteredEffects.value;
  if (list.length < 2) return;
  let idx = selectedIndexInFiltered.value;
  if (idx === -1) idx = 0;
  const next = (idx - 1 + list.length) % list.length;
  selectedEffectKey.value = getEffectUniqueKey(list[next]);
  handleEffectChange();
}

function goNextEffect() {
  if (sourceMode.value !== "effects") return;
  const list = filteredEffects.value;
  if (list.length < 2) return;
  let idx = selectedIndexInFiltered.value;
  if (idx === -1) idx = 0;
  const next = (idx + 1) % list.length;
  selectedEffectKey.value = getEffectUniqueKey(list[next]);
  handleEffectChange();
}

const selectedEffect = computed(() =>
  effectsList.value.find(
    (item) => getEffectUniqueKey(item) === selectedEffectKey.value
  )
);

function setSourceMode(mode) {
  if (sourceMode.value === mode) return;
  const prev = sourceMode.value;
  sourceMode.value = mode;
  if (mode === "json") {
    try {
      jsonInputText.value = JSON.stringify(rawData.value, null, 2);
    } catch {
      jsonInputText.value = "";
    }
    return;
  }
  if (mode === "jsonList") {
    if (prev === "json") {
      try {
        jsonListInputText.value = JSON.stringify([rawData.value], null, 2);
      } catch {
        jsonListInputText.value = "";
      }
    }
    return;
  }
  handleEffectChange();
}

function applyJsonToCanvas() {
  effectsError.value = "";
  let parsed;
  try {
    const raw = jsonInputText.value;
    parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch (e) {
    parseError.value = `JSON 解析失败：${e?.message || String(e)}`;
    nodes.value = [];
    edges.value = [];
    activeEffectMeta.value = null;
    return;
  }

  const workflow = parseWorkflowConfig(parsed);
  if (!workflow) {
    parseError.value =
      "JSON 结构无效：需要可解析为含 nodes 对象的 workflow（与效果 workflow_config 一致）。";
    nodes.value = [];
    edges.value = [];
    activeEffectMeta.value = null;
    return;
  }

  activeEffectMeta.value = {
    title: "自定义 JSON",
    effect_type: "json",
    code: "paste",
  };
  expandedState.clear();
  rawData.value = workflow;
  initGraph();
}

function renderJsonListAtIndex(idx) {
  const list = jsonListWorkflows.value;
  if (!list.length) return;
  const n = list.length;
  const i = ((idx % n) + n) % n;
  jsonListIndex.value = i;
  const workflow = list[i];
  activeEffectMeta.value = {
    title: workflow?.title || `列表项 ${i + 1}`,
    effect_type: "json_list",
    code: `list/${i + 1}`,
  };
  expandedState.clear();
  rawData.value = workflow;
  initGraph();
}

function applyJsonListToCanvas() {
  effectsError.value = "";
  let parsed;
  try {
    const raw = jsonListInputText.value;
    parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch (e) {
    parseError.value = `JSON 解析失败：${e?.message || String(e)}`;
    jsonListWorkflows.value = [];
    nodes.value = [];
    edges.value = [];
    activeEffectMeta.value = null;
    return;
  }

  if (!Array.isArray(parsed)) {
    parseError.value =
      "需要顶层为 JSON 数组 [...]，每个元素为含 nodes 的 workflow 对象。";
    jsonListWorkflows.value = [];
    nodes.value = [];
    edges.value = [];
    activeEffectMeta.value = null;
    return;
  }

  const workflows = [];
  for (let i = 0; i < parsed.length; i += 1) {
    const w = parseWorkflowConfig(parsed[i]);
    if (!w) {
      parseError.value = `第 ${
        i + 1
      } 个元素无效：需为含 nodes 对象的 workflow。`;
      jsonListWorkflows.value = [];
      nodes.value = [];
      edges.value = [];
      activeEffectMeta.value = null;
      return;
    }
    workflows.push(w);
  }

  if (!workflows.length) {
    parseError.value = "数组为空，请至少放入一个 workflow 对象。";
    jsonListWorkflows.value = [];
    nodes.value = [];
    edges.value = [];
    activeEffectMeta.value = null;
    return;
  }

  parseError.value = "";
  jsonListWorkflows.value = workflows;
  jsonListIndex.value = 0;
  renderJsonListAtIndex(0);
}

function goPrevJsonListItem() {
  if (sourceMode.value !== "jsonList") return;
  const list = jsonListWorkflows.value;
  if (list.length < 2) return;
  const idx = jsonListIndex.value;
  renderJsonListAtIndex(idx - 1);
}

function goNextJsonListItem() {
  if (sourceMode.value !== "jsonList") return;
  const list = jsonListWorkflows.value;
  if (list.length < 2) return;
  const idx = jsonListIndex.value;
  renderJsonListAtIndex(idx + 1);
}

// 搜索后自动选中第一条匹配项并刷新画布（仅效果列表模式）
watch(
  [searchKeyword, filteredEffects, sourceMode],
  () => {
    if (sourceMode.value !== "effects") return;
    const kw = searchKeyword.value.trim();
    if (!kw) return;
    const list = filteredEffects.value;
    if (!list.length) return;
    const firstKey = getEffectUniqueKey(list[0]);
    if (selectedEffectKey.value === firstKey) return;
    selectedEffectKey.value = firstKey;
    handleEffectChange();
  },
  { flush: "post" }
);

// 筛选 Video/Image 或列表变化后，当前选中若不在结果中则切到第一条
watch(
  [filteredEffects, sourceMode],
  () => {
    if (sourceMode.value !== "effects") return;
    const list = filteredEffects.value;
    if (!list.length) return;
    const key = selectedEffectKey.value;
    if (list.some((item) => getEffectUniqueKey(item) === key)) return;
    selectedEffectKey.value = getEffectUniqueKey(list[0]);
    handleEffectChange();
  },
  { flush: "post" }
);

// --- 展开/收起 触发重新布局 ---
const toggleExpand = (id) => {
  if (expandedState.has(id)) expandedState.delete(id);
  else expandedState.add(id);

  // 给 Vue DOM 一点时间渲染，然后重新计算 Dagre 布局
  nextTick(() => resetLayout());
};

const isExpanded = (id) => expandedState.has(id);

const getTypeLabel = (val) => {
  if (Array.isArray(val)) return "List";
  if (typeof val === "boolean") return "Bool";
  if (typeof val === "number") return "Num";
  return "String";
};

const isAppKeyOptionsValid = (options) =>
  Array.isArray(options) && options.length > 0;

const isAppKeyTypeValid = (type) => {
  if (type === null || type === undefined) return false;
  if (typeof type === "string" && type.trim() === "") return false;
  return true;
};

const shouldShowAppKeyEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  return isAppKeyOptionsValid(entry.options) || isAppKeyTypeValid(entry.type);
};

const getVisibleAppKeyEntries = (appKeyData) => {
  if (!appKeyData || typeof appKeyData !== "object") return [];
  return Object.entries(appKeyData).filter(([, entry]) =>
    shouldShowAppKeyEntry(entry)
  );
};

const formatAppKeyOptions = (options) => {
  if (!Array.isArray(options)) return JSON.stringify(options);
  try {
    return JSON.stringify(options);
  } catch {
    return String(options);
  }
};

const parseTemplateString = (str) => {
  if (typeof str !== "string") return [{ type: "text", content: str }];
  const regex = /\{\{\s*(.*?)\s*\}\}/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex)
      parts.push({ type: "text", content: str.slice(lastIndex, match.index) });
    parts.push({ type: "variable", content: match[1] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < str.length)
    parts.push({ type: "text", content: str.slice(lastIndex) });
  return parts;
};

const VariableText = defineComponent({
  props: ["text"],
  setup(props) {
    return () => {
      const parts = parseTemplateString(props.text);
      return h(
        "span",
        {
          class:
            "leading-relaxed whitespace-pre-wrap text-gray-600 break-words",
        },
        parts.map((part, i) => {
          if (part.type === "variable") {
            return h(
              "span",
              {
                class:
                  "inline-flex font-mono text-[10px] px-1.5 py-0.5 mx-0.5 bg-indigo-100 text-indigo-700 rounded border border-indigo-200",
              },
              `{ ${part.content} }`
            );
          }
          return h("span", null, part.content);
        })
      );
    };
  },
});

// 默认示例数据，实际会被 workflow_config 覆盖
const fallbackRawData = {
  title: "The user inputs 2 picture.",
  nodes: {
    genImage: {
      app: "10002",
      app_name: "replicate/google/nano-banana",
      input: {
        aspect_ratio: "{{ user_input.aspect_ratio }}",
        prompt:
          "将上传的人像与宠物照片融合成一张高级圣诞节杂志风人宠合照。\n人物与宠物靠近摆拍，面对面、鼻尖相贴或额头轻触，姿态自然亲昵。\n两者可佩戴简约的圣诞元素饰品，如圣诞帽、红色针织帽或驯鹿角发箍。\n背景为奶油色柔光色，带有暖色调光晕；\n画面采用浅景深，皮肤与毛发的细节柔和清晰；\n整体构图简洁而有质感，带有时尚写真风的精致感。\n画面上方可有绿色手写体“Merry Christmas”字样，\n整体色调温柔、高级、自然，呈现高端节日写真的质感。",
        image_input: "{{ user_input.image_input }}",
      },
      output: {
        result: "0",
      },
      tool_name: "image-to-image",
    },
    genVideo: {
      app: "10034",
      app_name: "wavespeed/vidu/q3",
      input: {
        resolution: "720p",
        aspectRatio: "9:16",
        duration: 5,
        prompt: "文字保持静止，人和宠物看向镜头微笑，缓慢运镜",
        promptOptimizer: false,
        image: "{{ genImage.result }}",
      },
      output: {
        result: "0",
      },
      depends: ["genImage"],
      tool_name: "image-to-video",
    },
  },
  output: {
    result: "{{ genVideo.result }}",
  },
};
const rawData = ref(fallbackRawData);

function normalizeEffectsList(data) {
  if (data == null) return [];
  try {
    if (typeof data === "string") {
      return normalizeEffectsList(JSON.parse(data));
    }
    if (Array.isArray(data)) return data;
    if (typeof data === "object") {
      return Object.values(data).flat();
    }
  } catch (error) {
    console.error("normalizeEffectsList 失败:", error);
  }
  return [];
}

function parseWorkflowConfig(workflowConfig) {
  let parsed = workflowConfig;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      console.error("workflow_config JSON 解析失败:", error);
      return null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== "object" ||
    typeof parsed.nodes !== "object"
  ) {
    return null;
  }

  return parsed;
}

async function loadEffectsData() {
  effectsLoading.value = true;
  effectsError.value = "";
  try {
    const [videoEffectsData, imageEffectsData] = await Promise.all([
      r2CachedFetch("/api/r2/ai/effects/video/index.json"),
      r2CachedFetch("/api/r2/ai/effects/image/index.json"),
    ]);

    const tagSource = (source) => (item) =>
      item && typeof item === "object"
        ? { ...item, _mediaSource: source }
        : item;

    effectsList.value = [
      ...normalizeEffectsList(videoEffectsData).map(tagSource("video")),
      ...normalizeEffectsList(imageEffectsData).map(tagSource("image")),
    ].filter((item) => item && typeof item === "object");

    if (effectsList.value.length === 0) {
      effectsError.value = "未获取到可用的 effects 数据。";
      return;
    }

    if (!selectedEffectKey.value) {
      selectedEffectKey.value = getEffectUniqueKey(effectsList.value[0]);
    }
    handleEffectChange();
  } catch (error) {
    console.error("加载 effects 配置出错:", error);
    effectsError.value = "加载 effects 配置失败。";
  } finally {
    effectsLoading.value = false;
  }
}

function handleEffectChange() {
  effectsError.value = "";
  const effect = selectedEffect.value;
  if (!effect) {
    effectsError.value = "未找到对应的 effect。";
    return;
  }

  const workflow = parseWorkflowConfig(effect.workflow_config);
  if (!workflow) {
    effectsError.value = `effect ${effect.effect_type || "-"} / ${
      effect.code || "-"
    } 的 workflow_config 无效。`;
    return;
  }

  activeEffectMeta.value = effect;
  expandedState.clear();
  rawData.value = workflow;
  initGraph();
}

/** 解析 {{ nodeId.outputKey }}，按最后一个点分割（支持 result、a 等任意输出名） */
function parseCrossNodeRef(varPath) {
  const s = String(varPath || "").trim();
  if (!s || s.startsWith("user_input.")) return null;
  const lastDot = s.lastIndexOf(".");
  if (lastDot <= 0) return null;
  const nodeId = s.slice(0, lastDot);
  const outputKey = s.slice(lastDot + 1);
  if (!nodeId || !outputKey) return null;
  return { nodeId, outputKey };
}

function getImplicitDependsSourceHandle(nodeData) {
  const out = nodeData?.output;
  if (out && typeof out === "object") {
    const keys = Object.keys(out);
    if (keys.includes("result")) return "result";
    if (keys.length) return keys[0];
  }
  return "result";
}

function encodeHandleKey(key) {
  return encodeURIComponent(String(key ?? ""));
}

function getSourceHandleId(key) {
  return `out:${encodeHandleKey(key)}`;
}

function getTargetHandleId(key) {
  return `in:${encodeHandleKey(key)}`;
}

function compareWorkflowFieldPath(a, b) {
  const aPath = String(a ?? "");
  const bPath = String(b ?? "");
  const aDepth = aPath.split(".").length;
  const bDepth = bPath.split(".").length;
  if (aDepth !== bDepth) return aDepth - bDepth;
  return aPath.localeCompare(bPath, "zh-Hans-CN", {
    numeric: true,
    sensitivity: "base",
  });
}

const VARIABLE_EDGE_COLORS = [
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#06b6d4",
  "#3b82f6",
  "#6366f1",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
  "#f43f5e",
];

function hashString(value) {
  const s = String(value ?? "");
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getVariableEdgeColor(seed) {
  const idx = hashString(seed) % VARIABLE_EDGE_COLORS.length;
  return VARIABLE_EDGE_COLORS[idx];
}

// --- 核心：数据清洗与双重依赖解析 ---
const parseDataToGraph = (data) => {
  const parsedNodes = [];
  const parsedEdges = [];
  const userInputsMap = new Set();
  if (!data?.nodes || typeof data.nodes !== "object") {
    return { parsedNodes: [], parsedEdges: [], error: "缺少有效的 nodes 对象" };
  }
  const nodeEntries = Object.entries(data.nodes);

  // 1. 通过 AST 扫描变量映射
  nodeEntries.forEach(([targetNodeId, nodeData]) => {
    Object.entries(nodeData?.input || {}).forEach(([targetParamKey, val]) => {
      const extractDependency = (str) => {
        const matches = [...str.matchAll(/\{\{\s*(.*?)\s*\}\}/g)];
        matches.forEach((m) => {
          const varPath = m[1].trim();

          if (varPath.startsWith("user_input.")) {
            const inputPath = varPath.replace("user_input.", "");
            const edgeColor = getVariableEdgeColor(
              `user_input.${inputPath}->${targetNodeId}.${targetParamKey}`
            );
            userInputsMap.add(inputPath);
            parsedEdges.push({
              id: `e-user-${inputPath}-to-${targetNodeId}-${targetParamKey}`,
              source: "global-user-input",
              sourceHandle: getSourceHandleId(inputPath),
              target: targetNodeId,
              targetHandle: getTargetHandleId(targetParamKey),
              animated: true,
              style: {
                stroke: edgeColor,
                strokeWidth: 3,
                strokeDasharray: "5,5",
              },
            });
          } else {
            const ref = parseCrossNodeRef(varPath);
            if (ref) {
              const edgeColor = getVariableEdgeColor(
                `${varPath}->${targetNodeId}.${targetParamKey}`
              );
              parsedEdges.push({
                id: `e-${ref.nodeId}-${ref.outputKey}-to-${targetNodeId}-${targetParamKey}`,
                source: ref.nodeId,
                sourceHandle: getSourceHandleId(ref.outputKey),
                target: targetNodeId,
                targetHandle: getTargetHandleId(targetParamKey),
                animated: true,
                style: { stroke: edgeColor, strokeWidth: 2.5 },
              });
            }
          }
        });
      };

      if (typeof val === "string") extractDependency(val);
      if (Array.isArray(val))
        val.forEach((v) => typeof v === "string" && extractDependency(v));
    });
  });

  // 2. 处理显式 depends 声明的兜底逻辑 (防止只传了 depends 但没使用变量时断线)
  nodeEntries.forEach(([nodeId, nodeData]) => {
    if (Array.isArray(nodeData.depends)) {
      nodeData.depends.forEach((depId) => {
        const depNode = data.nodes[depId];
        const outKey = getImplicitDependsSourceHandle(depNode || {});
        // 如果前面 AST 阶段没有建立过这两个节点间的线，我们就画一条隐式依赖线
        const hasEdge = parsedEdges.some(
          (e) => e.source === depId && e.target === nodeId
        );
        if (!hasEdge) {
          parsedEdges.push({
            id: `e-implicit-${depId}-to-${nodeId}`,
            source: depId,
            sourceHandle: getSourceHandleId(outKey),
            target: nodeId,
            targetHandle: GENERIC_TARGET_HANDLE_ID,
            animated: true,
            style: {
              stroke: "#9ca3af",
              strokeWidth: 2,
              strokeDasharray: "4,4",
            }, // 灰色虚线代表隐式依赖
          });
        }
      });
    }
  });

  // 3. 构建节点数据
  if (userInputsMap.size > 0) {
    parsedNodes.push({
      id: "global-user-input",
      type: "userInput",
      position: { x: 0, y: 0 },
      data: {
        outputs: Array.from(userInputsMap).sort(compareWorkflowFieldPath),
      },
    });
  }

  nodeEntries.forEach(([nodeId, nodeData]) => {
    const out =
      nodeData.output && typeof nodeData.output === "object"
        ? nodeData.output
        : null;
    const outputKeys =
      out && Object.keys(out).length > 0 ? Object.keys(out) : ["result"];
    parsedNodes.push({
      id: nodeId,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        title: nodeId,
        app: nodeData.app,
        tool_name: nodeData.tool_name,
        inputs: nodeData.input || {},
        app_name: nodeData.app_name,
        app_key_data: nodeData.app_key_data ?? nodeData.appKeyData,
        outputKeys,
        bgm_url: nodeData.bgmUrl,
      },
    });
  });

  // 4. 解析最终输出节点 (Root output)
  const outputData =
    data?.output && typeof data.output === "object" ? data.output : null;
  if (outputData) {
    parsedNodes.push({
      id: "global-final-output",
      type: "finalOutput",
      position: { x: 0, y: 0 },
      data: { outputs: outputData },
    });

    Object.entries(outputData).forEach(([key, val]) => {
      if (typeof val === "string") {
        const matches = [...val.matchAll(/\{\{\s*(.*?)\s*\}\}/g)];
        matches.forEach((m) => {
          const ref = parseCrossNodeRef(m[1]);
          if (!ref) return;
          const edgeColor = getVariableEdgeColor(
            `${m[1].trim()}->output.${key}`
          );
          parsedEdges.push({
            id: `e-final-${ref.nodeId}-${ref.outputKey}-to-output-${key}`,
            source: ref.nodeId,
            sourceHandle: getSourceHandleId(ref.outputKey),
            target: "global-final-output",
            targetHandle: getTargetHandleId(key),
            animated: true,
            style: { stroke: edgeColor, strokeWidth: 3 }, // 最终输出沿用变量色，便于追踪来源
          });
        });
      }
    });
  }

  return { parsedNodes, parsedEdges, error: null };
};

const estimateAppKeySectionHeight = (appKeyData) => {
  const rows = getVisibleAppKeyEntries(appKeyData).length;
  if (rows === 0) return 0;
  const header = 28;
  const perRow = 76;
  const raw = header + rows * perRow;
  const cap = 220;
  return Math.min(raw, cap);
};

// --- 动态节点高度估算算法 (专为 Dagre 引擎设计，解决重叠问题) ---
const estimateNodeHeight = (nodeId, inputs, appKeyData) => {
  let h = 80; // Header
  h += estimateAppKeySectionHeight(appKeyData);
  Object.entries(inputs || {}).forEach(([key, val]) => {
    h += 40; // Row spacing
    if (typeof val === "string") {
      // 核心修复：根据字符串长度动态估算高度，中文字符由于显示宽，估值需加大
      if (val.length > 80) {
        h += expandedState.has(`${nodeId}-${key}`)
          ? Math.ceil(val.length / 25) * 22
          : 66; // 展开 vs 折叠(line-clamp-3)
      } else {
        h += Math.ceil(val.length / 30) * 20;
      }
    } else if (Array.isArray(val)) {
      h += val.length * 40;
    } else {
      h += 20;
    }
  });
  return h + 40; // 底边距 padding
};

// --- 重新计算排版 ---
const layoutGraph = (rawNodes, rawEdges) => {
  const g = new dagre.graphlib.Graph();
  // nodesep 控制同级节点的垂直间距，ranksep 控制依赖层级的水平间距
  g.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 220, edgesep: 30 });
  g.setDefaultEdgeLabel(() => ({}));

  rawNodes.forEach((node) => {
    let height = 200;
    if (node.type === "userInput" || node.type === "finalOutput") {
      height = 120 + Object.keys(node.data.outputs).length * 50;
    } else if (node.type === "custom") {
      height = estimateNodeHeight(
        node.id,
        node.data.inputs,
        node.data.app_key_data
      );
    }
    g.setNode(node.id, { width: 380, height });
  });

  rawEdges.forEach((edge) => g.setEdge(edge.source, edge.target));
  dagre.layout(g);

  return rawNodes.map((node) => {
    const nodeWithPos = g.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPos.x - nodeWithPos.width / 2,
        y: nodeWithPos.y - nodeWithPos.height / 2,
      },
    };
  });
};

// --- 初始化与刷新 ---
const initGraph = () => {
  parseError.value = "";
  expandedState.clear();

  const { parsedNodes, parsedEdges, error } = parseDataToGraph(rawData.value);
  if (error) {
    parseError.value = error;
    nodes.value = [];
    edges.value = [];
    return;
  }
  if (!parsedNodes.length) {
    parseError.value = "nodes 为空，无法渲染";
    nodes.value = [];
    edges.value = [];
    return;
  }

  nodes.value = layoutGraph(parsedNodes, parsedEdges);
  edges.value = parsedEdges;
  nextTick(() => {
    updateNodeInternals(parsedNodes.map((node) => node.id));
    setTimeout(() => fitView({ padding: 0.1, duration: 800 }), 50);
  });
};

const resetLayout = () => {
  if (parseError.value) return;
  const layedOut = layoutGraph(nodes.value, edges.value);
  // 必须直接更新元素的 position 对象属性，触发 VueFlow 的平滑位移动画
  layedOut.forEach((n) => {
    const target = nodes.value.find((existing) => existing.id === n.id);
    if (target) target.position = n.position;
  });
  nextTick(() => {
    updateNodeInternals(nodes.value.map((node) => node.id));
    setTimeout(() => fitView({ padding: 0.1, duration: 800 }), 50);
  });
};

function isHotkeyTypingTarget(el) {
  if (!el || typeof el !== "object") return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/** Alt+←/→ 与 Q/E：效果列表、JSON 列表下切换上一条/下一条（输入框内不触发） */
function onWorkflowHotkeys(e) {
  if (e.repeat) return;
  if (isHotkeyTypingTarget(e.target)) return;

  if (e.altKey) {
    if (sourceMode.value === "effects") {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrevEffect();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNextEffect();
      }
    } else if (sourceMode.value === "jsonList") {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrevJsonListItem();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNextJsonListItem();
      }
    }
    return;
  }

  if (e.ctrlKey || e.metaKey) return;
  const k = typeof e.key === "string" ? e.key.toLowerCase() : "";
  if (k !== "q" && k !== "e") return;

  if (sourceMode.value === "effects") {
    e.preventDefault();
    if (k === "q") goPrevEffect();
    else goNextEffect();
    return;
  }
  if (sourceMode.value === "jsonList") {
    e.preventDefault();
    if (k === "q") goPrevJsonListItem();
    else goNextJsonListItem();
  }
}

onMounted(async () => {
  initGraph();
  await loadEffectsData();
  window.addEventListener("keydown", onWorkflowHotkeys);
});

onUnmounted(() => {
  window.removeEventListener("keydown", onWorkflowHotkeys);
});
</script>

<style scoped>
.panel-toggle-btn {
  box-sizing: border-box;
  min-width: 7.25rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  border-radius: 0.375rem;
  border: 1px solid rgb(203 213 225);
  background-color: #fff;
  color: rgb(51 65 85);
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
  text-align: center;
}
.panel-toggle-btn:hover {
  background-color: rgb(248 250 252);
}
</style>

<style>
.vue-flow__handle {
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.vue-flow__handle:hover {
  transform: scale(1.4);
  z-index: 50;
}
.line-clamp-3 {
  display: -webkit-box;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
